import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  HiOutlineCamera,
  HiOutlineEnvelope,
  HiOutlinePencilSquare,
  HiOutlineShieldExclamation,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import apiClient from "../api/apiClient";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { handleErrors } from "../modules/handleErrors";
import ConfirmDeleteModal from "../modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import { TbArrowBackUp } from "react-icons/tb";
import { logout } from "../redux/authSlice";

const getInitials = (name, email) => {
  const source = (name || email || "U").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const UserInfo = () => {
  const { userid } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profiledata, fetchDataprofile } = useIndexPageData();
  const [avatarSrc, setAvatarSrc] = useState("");
  const [loader, setloader] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDraft, setEditDraft] = useState({ full_name: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);

  const [profileupdate, setprofileupdate] = useState({
    full_name: "",
    profile: "",
    promotional_emails: false,
  });

  const handleUploadClick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const formData = new FormData();
    formData.append("profile", file);

    postprofiledata(formData);

    reader.onloadend = () => {
      setAvatarSrc(reader.result);
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const postprofiledata = async (formdata, { onSuccess } = {}) => {
    if (!userid) return false;

    try {
      setloader(true);
      const result = await apiClient.put(`/register/${userid}/`, formdata);
      setloader(false);
      if (!result.ok) {
        handleErrors(result.data);
        return false;
      }

      fetchDataprofile();
      toast.success("Profile updated successfully");
      onSuccess?.();
      return true;
    } catch (error) {
      setloader(false);
      console.error("Error updating profile:", error.message);
      return false;
    }
  };

  useEffect(() => {
    if (!profiledata || Array.isArray(profiledata) || typeof profiledata !== "object") {
      return;
    }

    setprofileupdate({
      full_name: profiledata.full_name ?? "",
      profile: profiledata.profile ?? "",
      email: profiledata.email ?? "",
      promotional_emails: Boolean(profiledata.promotional_emails),
    });
    setAvatarSrc("");
  }, [profiledata]);

  const handlePromotionalEmailsChange = async (checked) => {
    if (Boolean(profiledata?.promotional_emails) === checked) return;

    setprofileupdate((prev) => ({ ...prev, promotional_emails: checked }));

    const formData = new FormData();
    formData.append("promotional_emails", checked);
    await postprofiledata(formData);
  };

  const openEditModal = () => {
    setEditDraft({ full_name: profiledata?.full_name ?? "" });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditDraft({ full_name: profiledata?.full_name ?? "" });
  };

  const handleApplyEdit = async (e) => {
    e.preventDefault();
    const trimmed = editDraft.full_name?.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    if (trimmed.length > 17) {
      toast.error("Name must be 17 characters or less");
      return;
    }
    if (trimmed === profiledata?.full_name) {
      closeEditModal();
      return;
    }

    const formData = new FormData();
    formData.append("full_name", trimmed);
    await postprofiledata(formData, { onSuccess: closeEditModal });
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteAccountConfirm = async () => {
    try {
      setDeleteLoader(true);
      const result = await apiClient.post(`/profile/delete-account/`);
      setDeleteLoader(false);

      if (!result.ok) {
        handleErrors(result.data);
        return;
      }

      toast.success("Account deleted successfully");
      dispatch(logout());
      navigate("/");
    } catch (error) {
      setDeleteLoader(false);
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account. Please try again.");
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const avatarImage = avatarSrc || profileupdate.profile;
  const displayName = profileupdate?.full_name || "Your name";
  const displayEmail = profileupdate?.email || "—";
  const initials = getInitials(profileupdate?.full_name, profileupdate?.email);

  return (
    <div className="user-profile">
      <div className="container py-4 py-md-5">
        <header className="user-profile__header">
          <button
            type="button"
            className="user-profile__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <TbArrowBackUp size={22} aria-hidden="true" />
          </button>
          <div className="user-profile__header-copy">
            <span className="user-profile__eyebrow">Account</span>
            <h1 className="user-profile__title">Profile Settings</h1>
            <p className="user-profile__subtitle">
              Manage your photo, personal details, and email preferences.
            </p>
          </div>
        </header>

        <div className="user-profile__layout">
          <aside className="user-profile__sidebar">
            <div className="user-profile__card user-profile__card--identity">
              <div className="user-profile__avatar-block">
                <div
                  className={`user-profile__avatar${
                    loader ? " user-profile__avatar--loading" : ""
                  }`}
                >
                  {avatarImage ? (
                    <img src={avatarImage} alt={displayName} />
                  ) : (
                    <span className="user-profile__avatar-initials">
                      {initials}
                    </span>
                  )}
                  {loader && (
                    <span className="user-profile__avatar-overlay">
                      Uploading…
                    </span>
                  )}
                </div>

                <input
                  hidden
                  accept="image/*"
                  id="profile-photo-upload"
                  type="file"
                  onChange={handleUploadClick}
                  disabled={loader}
                />
                <label
                  htmlFor="profile-photo-upload"
                  className={`user-profile__upload-btn${
                    loader ? " user-profile__upload-btn--disabled" : ""
                  }`}
                >
                  <HiOutlineCamera size={18} aria-hidden="true" />
                  Change photo
                </label>
              </div>

              <div className="user-profile__identity-meta">
                <h2 className="user-profile__identity-name">{displayName}</h2>
                <p className="user-profile__identity-email">{displayEmail}</p>
              </div>

              <div className="user-profile__identity-badge">
                <HiOutlineUserCircle size={18} aria-hidden="true" />
                Buyer account
              </div>
            </div>
          </aside>

          <div className="user-profile__main">
            <section className="user-profile__card">
              <div className="user-profile__card-head">
                <h3 className="user-profile__card-title">Personal information</h3>
                <p className="user-profile__card-desc">
                  Your name appears on downloads and account activity.
                </p>
              </div>

              <div className="user-profile__fields">
                <div className="user-profile__field">
                  <span className="user-profile__field-label">Display name</span>
                  <div className="user-profile__field-value-row">
                    <span className="user-profile__field-value">
                      {profileupdate?.full_name || "—"}
                    </span>
                    <button
                      type="button"
                      className="user-profile__edit-btn"
                      onClick={openEditModal}
                      aria-label="Edit name"
                    >
                      <HiOutlinePencilSquare size={18} aria-hidden="true" />
                      Edit
                    </button>
                  </div>
                </div>

                <div className="user-profile__field">
                  <span className="user-profile__field-label">
                    Email address
                  </span>
                  <div className="user-profile__field-value-row">
                    <span className="user-profile__field-value user-profile__field-value--with-icon">
                      <HiOutlineEnvelope size={18} aria-hidden="true" />
                      {displayEmail}
                    </span>
                  </div>
                  <span className="user-profile__field-hint">
                    Used for login and order receipts. Contact support to change.
                  </span>
                </div>
              </div>
            </section>

            <section className="user-profile__card">
              <div className="user-profile__card-head">
                <h3 className="user-profile__card-title">Email preferences</h3>
                <p className="user-profile__card-desc">
                  Choose what updates you receive from Mockee.
                </p>
              </div>

              <label className="user-profile__pref" htmlFor="agreeTerms">
                <input
                  type="checkbox"
                  className="user-profile__pref-input"
                  id="agreeTerms"
                  name="terms"
                  checked={!!profileupdate.promotional_emails}
                  onChange={(e) => {
                    handlePromotionalEmailsChange(e.target.checked);
                  }}
                />
                <span className="user-profile__pref-box" aria-hidden="true" />
                <span className="user-profile__pref-copy">
                  <span className="user-profile__pref-text">
                    I agree to receive commercial information about news and
                    commercial offers within the &quot;Mockee&quot; Platform,
                    sent by Kacper Fertacz to the e-mail address I provided. In
                    addition, I declare that I have read the{" "}
                    <a href="/terms-and-conditions">Terms and Conditions</a> and
                    the <a href="/privacy-policy">Privacy Policy</a> and accept
                    their provisions.
                  </span>
                </span>
              </label>
            </section>

            <section className="user-profile__card user-profile__card--danger">
              <div className="user-profile__card-head">
                <div className="user-profile__danger-head">
                  <span className="user-profile__danger-icon" aria-hidden="true">
                    <HiOutlineShieldExclamation size={22} />
                  </span>
                  <div>
                    <h3 className="user-profile__card-title user-profile__card-title--danger">
                      Delete account
                    </h3>
                    <p className="user-profile__card-desc user-profile__card-desc--danger">
                      Permanently remove your account and all associated data.
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="user-profile__danger-btn"
                onClick={handleDeleteAccountClick}
                disabled={deleteLoader}
              >
                {deleteLoader ? "Deleting…" : "Delete my account"}
              </button>
            </section>
          </div>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        centered
        backdrop="static"
        contentClassName="user-profile__modal border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="user-profile__modal-title">
            Edit display name
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <form onSubmit={handleApplyEdit}>
            <div className="user-profile__modal-field">
              <label htmlFor="edit-full-name" className="user-profile__field-label">
                Name
              </label>
              <input
                id="edit-full-name"
                type="text"
                className="user-profile__input"
                value={editDraft.full_name}
                maxLength={17}
                onChange={(e) =>
                  setEditDraft({ full_name: e.target.value })
                }
                autoFocus
                placeholder="Your display name"
              />
              <span className="user-profile__field-hint">
                Maximum 17 characters
              </span>
            </div>

            <div className="user-profile__modal-field">
              <span className="user-profile__field-label">Email</span>
              <p className="user-profile__modal-readonly">{profileupdate?.email}</p>
              <span className="user-profile__field-hint">
                Email cannot be changed here.
              </span>
            </div>

            <div className="user-profile__modal-actions">
              <button
                type="button"
                className="user-profile__btn user-profile__btn--ghost"
                onClick={closeEditModal}
                disabled={loader}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="user-profile__btn user-profile__btn--primary"
                disabled={loader}
              >
                {loader ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={handleDeleteModalClose}
        onConfirm={handleDeleteAccountConfirm}
        user={profiledata}
        deleting={deleteLoader}
      />
    </div>
  );
};

export default UserInfo;
